import type { ReactElement } from 'react';
import { useNavigate } from 'react-router';
import { useSeriesList } from '../api/queries';
import {
  Balances,
  Broken,
  Card,
  Empty,
  Icon,
  ListSwitch,
  Rows,
  SearchField,
  SkeletonRows,
} from '../ui';
import { duration } from '../lib/time';
import { seriesWhen } from '../series/when';
import { useUrlText } from '../lib/url-tab';

/**
 * Every meeting that repeats.
 *
 * ⚠ It was a tab on **People** — "very weird logic", and right: a series is
 * not a who. Then it was reached from a quiet link in the Meetings filter row,
 * which Vladimir could not find at all. It sits beside Meetings now, on a
 * switch at the same weight as the word Meetings, because a series is a
 * meeting that repeats and that is where somebody looks for it.
 *
 * Worth knowing why this list is short now: until 2026-09-21 a one-off meeting
 * was given a series key of its own, so this page had a row per meeting and
 * said nothing. Only genuinely recurring meetings — and the ones somebody put
 * together by hand — are series.
 */
export function SeriesIndex(): ReactElement {
  const series = useSeriesList();
  const navigate = useNavigate();
  const [filter, setFilter] = useUrlText('q');
  const q = filter.trim().toLowerCase();
  const shown = (series.data ?? []).filter(
    (s) => !q || s.title.toLowerCase().includes(q) || s.seriesKey.toLowerCase().includes(q),
  );

  const open = (key: string) => navigate(`/series/${encodeURIComponent(key)}`);

  return (
    <div className="page stack-lg">
      <ListSwitch on="series" />
      <div className="page-head">
        <div className="stack" style={{ gap: 4 }}>
          <h1 className="t-title">Series</h1>
          <p className="mono">
            {shown.length} {shown.length === 1 ? 'series' : 'series'} · a meeting that repeats
          </p>
        </div>
        <div style={{ width: 260 }}>
          <SearchField
            placeholder="Filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            kbd=""
          />
        </div>
      </div>

      <Card flush>
        {series.isPending ? (
          <SkeletonRows rows={4} />
        ) : series.isError ? (
          <Broken what="Series" retry={() => void series.refetch()} />
        ) : shown.length === 0 ? (
          <Empty lead={q ? 'Nothing matches.' : 'No series yet.'}>
            {q
              ? 'Try a different word.'
              : 'A recurring meeting becomes a series on its own once two of its instances have been recorded — and you can put one together yourself from any meeting’s page, under its title.'}
          </Empty>
        ) : (
          <Rows>
            {shown.map((s) => (
              <div
                key={s.seriesKey}
                className="row"
                role="link"
                tabIndex={0}
                onClick={() => open(s.seriesKey)}
                onKeyDown={(e) => e.key === 'Enter' && open(s.seriesKey)}
                style={{ cursor: 'pointer', minHeight: 60 }}
              >
                <span className="avatar room lg">
                  <Icon name="space" size={17} />
                </span>
                <div className="row-main">
                  <span className="row-title">{s.title}</span>
                  <span className="mono">
                    {s.instances
                      ? `${s.instances} ${s.instances === 1 ? 'instance' : 'instances'} · ${duration(s.totalSeconds)}`
                      : seriesWhen(s)}{' '}
                    · {s.language === 'auto' ? 'your language' : s.language} ·{' '}
                    {s.botEnabled ? 'recording' : 'not recording'}
                  </span>
                </div>
                <Balances
                  iOwe={s.open.youOwe}
                  theyOwe={s.open.owedToYou}
                  to={`/open?series=${encodeURIComponent(s.seriesKey)}`}
                />
              </div>
            ))}
          </Rows>
        )}
      </Card>
    </div>
  );
}
